import {
  ArrowPathRoundedSquareIcon,
  ClipboardIcon,
  RectangleStackIcon,
  UserMinusIcon,
} from "@heroicons/react/24/outline";
import ReactTooltip from "react-tooltip";
import type { ReportSummaryItemApi } from "../../api/Reports";
import type { ActiveWorkflowStepRunApi } from "../../api/ReportsApi";
import {
  WORKFLOW_MANUAL_SCRAPE_STATUS,
  WORKFLOW_STEP_STATUS_ENUM,
  workStepNameToUI,
} from "../../api/Workflows";
import { classNames, formattedISODate, formattedShortTime } from "../../utils";
import NotLoadedYet from "../../shared-components/NotLoadedYet";
import TableReportDateWithFormat from "../../shared-components/TableReportDateWithFormat";
import SecurityLockIcon from "../../shared-components/icons/SecurityLockIcon";
import MisconductCancelButton from "../MisconductCancelButton";
import ReportDetailLink from "../ReportDetailLink";
import ReportStatus from "../ReportStatus";
import ReportStepsModal from "../ReportStepsModal";
import { reportFailed } from "../utils";
import {
  ReportEntryStyle,
  ReportRowItem,
} from "../helper-components/TableModels";

export type InProcessV2026RowOpts = {
  stepLoading?: boolean;
  canManageReports: boolean;
  canCancelMisconduct: boolean;
  canPostRate: boolean;
  canPostScrape: boolean;
  canConfirmProfile: boolean;
  canInternalQaEdit: boolean;
  canClientQaEdit: boolean;
  canNavAmendment: boolean;
  canEditAmendment360: boolean;
  /** When true, only assigned workflow step names are clickable. */
  hasTaskAssignment: boolean;
  assignedStepNames: Set<string>;
  assignedBlueprintUuids: Set<string>;
  failedLockWorkflowRuns: Set<string>;
  onReportClick: (report: ReportSummaryItemApi) => void;
  onUnlockSuccess: (workflowRunUuid: string) => void;
  /** When false, hide lazy in-progress step links/icons under the status badge. */
  showActiveSteps?: boolean;
};

export type InProcessStepPermissions = Pick<
  InProcessV2026RowOpts,
  | "canPostRate"
  | "canPostScrape"
  | "canConfirmProfile"
  | "canInternalQaEdit"
  | "canClientQaEdit"
  | "canNavAmendment"
  | "canEditAmendment360"
>;

export type InProcessTaskAssignment = Pick<
  InProcessV2026RowOpts,
  "hasTaskAssignment" | "assignedStepNames" | "assignedBlueprintUuids"
>;

export type StepLinkAccess = {
  hasPermission: boolean;
  isAssigned: boolean;
  isBlueprintAssigned: boolean;
  canOpen: boolean;
};

export type InProcessV2026Report = ReportSummaryItemApi & {
  active_steps?: ActiveWorkflowStepRunApi[];
  selected_step_ui_link?: string | null;
  /** Step from GET /lists/misconduct/in_process, not the lazy overlay. */
  list_step_name?: string | null;
  list_step_payload?: ReportSummaryItemApi["step_payload"];
};

export function mergeLatestStepIntoReport(
  listRow: ReportSummaryItemApi,
  step:
    | {
        step_name: string;
        payload: Record<string, unknown>;
        active_steps?: ActiveWorkflowStepRunApi[];
      }
    | undefined
): InProcessV2026Report {
  const listStep = {
    list_step_name: listRow.step_name,
    list_step_payload: listRow.step_payload,
  };
  if (!step) {
    return { ...listRow, ...listStep };
  }
  return {
    ...listRow,
    ...listStep,
    step_name: step.step_name,
    step_payload: step.payload,
    active_steps: step.active_steps,
  };
}

/** Name/uuid clicks use the page-level list step, never a lazy-selected ui_link. */
export function reportForListQueryClick(
  report: InProcessV2026Report
): ReportSummaryItemApi {
  const { selected_step_ui_link: _ignored, ...rest } = report;
  return {
    ...rest,
    step_name: report.list_step_name ?? report.step_name,
    step_payload: report.list_step_payload ?? report.step_payload,
  };
}

export function checkInProcessStepPermission(
  step_name: string | null,
  step_payload: ReportSummaryItemApi["step_payload"],
  permissions: InProcessStepPermissions
): boolean {
  const {
    canPostRate,
    canPostScrape,
    canConfirmProfile,
    canInternalQaEdit,
    canClientQaEdit,
    canNavAmendment,
    canEditAmendment360,
  } = permissions;
  return (
    (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualRate && canPostRate) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualScrape &&
      step_payload?.MANUAL_SCRAPE_READY ===
        WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS &&
      canPostScrape) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.ProfilesManualRate &&
      canConfirmProfile) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.WebContentReview && canPostRate) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.InternalQA && canInternalQaEdit) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.ClientQA && canClientQaEdit) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.PostsManualEdit &&
      canNavAmendment) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.ArticlesManualEdit &&
      canNavAmendment) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.AmendmentQA && canNavAmendment) ||
    (step_name === WORKFLOW_STEP_STATUS_ENUM.ThreeSixtyAmend &&
      canEditAmendment360)
  );
}

/** Whether the user may follow this step's configured ui_link. */
export function canNavigateStepUiLink(
  step: Pick<ActiveWorkflowStepRunApi, "step_name" | "payload" | "ui_link">,
  permissions: InProcessStepPermissions
): boolean {
  const link = (step.ui_link ?? "").toLowerCase();
  if (!link) return false;
  if (link.includes("/post-rate") || link.includes("/webcontent")) {
    return permissions.canPostRate;
  }
  if (link.includes("/scrape")) {
    return (
      permissions.canPostScrape &&
      step.payload?.MANUAL_SCRAPE_READY ===
        WORKFLOW_MANUAL_SCRAPE_STATUS.IN_PROGRESS
    );
  }
  if (link.includes("/detail")) {
    return permissions.canConfirmProfile;
  }
  if (link.includes("/iqa")) {
    return permissions.canInternalQaEdit;
  }
  if (link.includes("/cqa")) {
    return permissions.canClientQaEdit;
  }
  if (
    link.includes("/post-edit") ||
    link.includes("/article-edit") ||
    link.includes("/aqa")
  ) {
    return permissions.canNavAmendment;
  }
  if (link.includes("/amendment/360")) {
    return permissions.canEditAmendment360;
  }
  return checkInProcessStepPermission(
    step.step_name,
    step.payload,
    permissions
  );
}

/** Task assignment only applies when the actor has_task; otherwise every step is in scope. */
export function isStepAssignedToActor(
  stepName: string | null | undefined,
  assignment: InProcessTaskAssignment
): boolean {
  if (!assignment.hasTaskAssignment) return true;
  if (!stepName) return false;
  return assignment.assignedStepNames.has(stepName);
}

export function isBlueprintAssignedToActor(
  blueprintUuid: string | null | undefined,
  assignment: InProcessTaskAssignment
): boolean {
  if (!assignment.hasTaskAssignment) return true;
  if (!blueprintUuid) return false;
  return assignment.assignedBlueprintUuids.has(blueprintUuid.toLowerCase());
}

export function evaluateStepLinkAccess(
  step: Pick<ActiveWorkflowStepRunApi, "step_name" | "payload" | "ui_link">,
  permissions: InProcessStepPermissions,
  assignment: InProcessTaskAssignment,
  blueprintUuid?: string | null
): StepLinkAccess {
  const hasPermission = canNavigateStepUiLink(step, permissions);
  const isAssigned = isStepAssignedToActor(step.step_name, assignment);
  const isBlueprintAssigned = isBlueprintAssignedToActor(
    blueprintUuid,
    assignment
  );
  return {
    hasPermission,
    isAssigned,
    isBlueprintAssigned,
    canOpen: hasPermission && isAssigned && isBlueprintAssigned,
  };
}

/** In-progress steps to list in the Status cell, including those with no ui_link. */
export function visibleActiveSteps(
  activeSteps: ActiveWorkflowStepRunApi[] | undefined
): ActiveWorkflowStepRunApi[] {
  return activeSteps ?? [];
}

function stepHasUiLink(
  step: Pick<ActiveWorkflowStepRunApi, "ui_link">
): boolean {
  return Boolean(step.ui_link);
}

/** First active step the user could open with no blocking icon, if any. */
export function firstOpenActiveStep(
  activeSteps: ActiveWorkflowStepRunApi[],
  permissions: InProcessStepPermissions,
  assignment: InProcessTaskAssignment,
  blueprintUuid?: string | null
): ActiveWorkflowStepRunApi | undefined {
  return activeSteps.find(
    (step) =>
      stepHasUiLink(step) &&
      evaluateStepLinkAccess(step, permissions, assignment, blueprintUuid)
        .canOpen
  );
}

function stepDisplayName(stepName: string): string {
  return (
    workStepNameToUI(stepName as WORKFLOW_STEP_STATUS_ENUM)?.name ?? stepName
  );
}

function inProcessStatusCell(
  report: InProcessV2026Report,
  stepName: string | null,
  stepLoading: boolean,
  activeSteps: ActiveWorkflowStepRunApi[],
  permissions: InProcessStepPermissions,
  assignment: InProcessTaskAssignment,
  onReportClick: (report: ReportSummaryItemApi) => void,
  showActiveSteps: boolean
) {
  if (stepLoading) {
    return (
      <>
        <NotLoadedYet className="h-6 w-[12ch]" />
        <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
      </>
    );
  }

  if (reportFailed(report)) {
    return (
      <>
        <ReportStatus
          stepStatusName={null}
          failed={true}
          report={report}
          allowResumeOnClick={true}
        />
        <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
      </>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col items-start gap-1">
      <div className="flex w-full min-w-0 items-center gap-2">
        <ReportStatus
          stepStatusName={stepName}
          failed={false}
          report={report}
          allowResumeOnClick={true}
        />
        <ReportStepsModal workflowRunUUID={report.workflow_run_uuid} />
      </div>
      {showActiveSteps && (
        <div className="flex w-full min-w-0 flex-col items-start gap-0.5">
          {activeSteps.map((step) => {
            const label = stepDisplayName(step.step_name);
            if (!stepHasUiLink(step)) {
              return (
                <span
                  key={step.external_id}
                  className="max-w-full truncate text-sm text-gray-700"
                  title={label}
                >
                  {label}
                </span>
              );
            }
            const access = evaluateStepLinkAccess(
              step,
              permissions,
              assignment,
              report.blueprint_uuid
            );
            if (!access.canOpen) {
              const reasons: string[] = [];
              if (!access.hasPermission) {
                reasons.push("You do not have permission to open this step");
              }
              if (!access.isAssigned) {
                reasons.push("This step is not assigned to you");
              }
              if (!access.isBlueprintAssigned) {
                reasons.push("This report's blueprint is not assigned to you");
              }
              return (
                <span
                  key={step.external_id}
                  className="flex max-w-full items-center gap-1 text-sm text-gray-500"
                  title={reasons.join(". ")}
                >
                  {!access.hasPermission && (
                    <SecurityLockIcon
                      width="1rem"
                      height="1rem"
                      className="shrink-0 text-gray-500"
                      data-testid="step-permission-lock"
                    />
                  )}
                  {!access.isAssigned && (
                    <UserMinusIcon
                      className="h-4 w-4 shrink-0 text-gray-500"
                      data-testid="step-assignment-lock"
                      aria-hidden="true"
                    />
                  )}
                  {!access.isBlueprintAssigned && (
                    <RectangleStackIcon
                      className="h-4 w-4 shrink-0 text-gray-500"
                      data-testid="step-blueprint-lock"
                      aria-hidden="true"
                    />
                  )}
                  <span className="truncate">{label}</span>
                </span>
              );
            }
            return (
              <button
                key={step.external_id}
                type="button"
                title={label}
                className="max-w-full truncate text-left text-sm font-semibold text-blue-700 underline"
                onClick={() =>
                  onReportClick({
                    ...report,
                    step_name: step.step_name,
                    step_payload: step.payload,
                    selected_step_ui_link: step.ui_link,
                  } as InProcessV2026Report)
                }
              >
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function buildInProcessV2026TableRow(
  report: InProcessV2026Report,
  opts: InProcessV2026RowOpts
): ReportRowItem<ReportSummaryItemApi> {
  const {
    stepLoading = false,
    canManageReports,
    canCancelMisconduct,
    canPostRate,
    canPostScrape,
    canConfirmProfile,
    canInternalQaEdit,
    canClientQaEdit,
    canNavAmendment,
    canEditAmendment360,
    hasTaskAssignment,
    assignedStepNames,
    assignedBlueprintUuids,
    failedLockWorkflowRuns,
    onReportClick,
    onUnlockSuccess,
    showActiveSteps = false,
  } = opts;

  const isErrorRow = reportFailed(report);
  const lazyStepName = isErrorRow ? null : report.step_name || null;
  const listQueryReport = reportForListQueryClick(report);
  const listStepName = isErrorRow ? null : listQueryReport.step_name || null;

  const stepPermissions: InProcessStepPermissions = {
    canPostRate,
    canPostScrape,
    canConfirmProfile,
    canInternalQaEdit,
    canClientQaEdit,
    canNavAmendment,
    canEditAmendment360,
  };
  const assignment: InProcessTaskAssignment = {
    hasTaskAssignment,
    assignedStepNames,
    assignedBlueprintUuids,
  };
  const listStepPermission =
    checkInProcessStepPermission(
      listStepName,
      listQueryReport.step_payload,
      stepPermissions
    ) &&
    isStepAssignedToActor(listStepName, assignment) &&
    isBlueprintAssignedToActor(report.blueprint_uuid, assignment);
  const activeSteps = visibleActiveSteps(report.active_steps);
  // The name link prefers a lazy step the user can actually open; otherwise it
  // keeps following the page-level list step.
  const openStep = isErrorRow
    ? undefined
    : firstOpenActiveStep(
        activeSteps,
        stepPermissions,
        assignment,
        report.blueprint_uuid
      );
  const nameReport: InProcessV2026Report = openStep
    ? {
        ...report,
        step_name: openStep.step_name,
        step_payload: openStep.payload,
        selected_step_ui_link: openStep.ui_link,
      }
    : listQueryReport;
  const nameLinkEnabled = openStep
    ? canManageReports
    : canManageReports && listStepPermission;

  const rowData = [
    {
      text: report.full_name,
      style: ReportEntryStyle.COMPONENT,
      component: (
        <ReportDetailLink
          report={nameReport}
          onClick={onReportClick}
          disabled={!nameLinkEnabled}
          forceLocked={failedLockWorkflowRuns.has(report.workflow_run_uuid)}
          onUnlockSuccess={() => onUnlockSuccess(report.workflow_run_uuid)}
        />
      ),
    },
    {
      text: report.workflow_status,
      style: ReportEntryStyle.COMPONENT,
      component: inProcessStatusCell(
        report,
        showActiveSteps ? lazyStepName : listStepName,
        showActiveSteps && stepLoading,
        activeSteps,
        stepPermissions,
        assignment,
        onReportClick,
        showActiveSteps
      ),
      customClass: "w-48",
    },
    {
      component: (
        <div className="flex flex-col">
          <span className="border-b-2">{report.blueprint_name}</span>
          <small className="block">
            {report.behavior_kit_name ?? "General"}
          </small>
        </div>
      ),
      text:
        report.blueprint_name + " " + (report.behavior_kit_name ?? "General"),
      style: ReportEntryStyle.COMPONENT,
    },
    {
      text: formattedISODate(report.report_date_added),
      component: report.recur ? (
        <>
          <TableReportDateWithFormat
            date={report.report_date_added}
            updated={false}
            unique_id={report.report_uuid}
          />
          <span
            data-tip="Employee Screening"
            data-tooltip-id={`recur-tip-${report.report_uuid}`}
            className="cursor-pointer"
          >
            <ArrowPathRoundedSquareIcon className="w-5 text-gray-500" />
          </span>
          <ReactTooltip id={`recur-tip-${report.report_uuid}`} effect="solid" />
        </>
      ) : (
        <TableReportDateWithFormat
          date={report.report_date_added}
          updated={false}
        />
      ),
      style: ReportEntryStyle.COMPONENT,
      customClass: "flex-1 whitespace-pre",
    },
    {
      text: formattedShortTime(report.due_date),
      style: ReportEntryStyle.COMPONENT,
      component: (
        <TableReportDateWithFormat
          date={report.due_date}
          updated={report.due_date_updated}
        />
      ),
      customClass: "flex-1 whitespace-pre",
    },
    {
      text: report.report_uuid,
      style: ReportEntryStyle.COMPONENT,
      component: (
        <div className="flex gap-1">
          <ClipboardIcon
            onClick={() => {
              navigator.clipboard.writeText(report.report_uuid);
            }}
            className="w-4 cursor-pointer"
          />
          <span
            onClick={() => onReportClick(listQueryReport)}
            className={classNames(
              "w-16 h-[30px] truncate",
              canManageReports && listStepPermission
                ? "underline cursor-pointer"
                : ""
            )}
          >
            {report.report_uuid}
          </span>
        </div>
      ),
    },
    {
      text: `${report.client_report_id}`,
      style: ReportEntryStyle.TEXT,
    },
  ];

  if (canCancelMisconduct) {
    rowData.push({
      text: "Cancel",
      style: ReportEntryStyle.COMPONENT,
      component: <MisconductCancelButton reportUUID={report.report_uuid} />,
    });
  }

  return {
    report,
    report_uuid: report.report_uuid,
    isLocked:
      report.is_locked || failedLockWorkflowRuns.has(report.workflow_run_uuid),
    data: rowData,
  };
}
